import React, { useCallback, useEffect, useRef, useState } from "react"

const SCROLL_SPEED_PX = 6
const SCROLL_INTERVAL_MS = 16

// Drop this inside a `position: relative` wrapper alongside the scrollable
// element `scrollRef` points to. Shows an up/down arrow when there's more
// content to scroll to in that direction; hovering anywhere over the arrow's
// edge zone auto-scrolls the container for as long as the cursor stays there.
export default function ScrollArrows({ scrollRef }) {
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const scrollIntervalRef = useRef(null)
  const topZoneRef = useRef(null)
  const bottomZoneRef = useRef(null)
  const bottomBoundaryElRef = useRef(null)
  const hasBoundaryRef = useRef(false)

  // How much further (in px) the container could still usefully scroll down.
  // When a footer boundary was found (see updateScrollState), this measures
  // the gap between the last real content element's bottom edge and that
  // boundary — using the last child's actual rendered position, rather than
  // scrollHeight minus some guessed padding amount, so it's correct
  // regardless of how much trailing padding a container reserves for footer
  // clearance: padding that still has real content behind it stays
  // scrollable, and pure dead padding past the last element doesn't.
  // Otherwise, falls back to the plain scrollHeight/clientHeight check used
  // everywhere else this component is dropped in.
  const remainingScrollablePx = (el) => {
    if (!hasBoundaryRef.current) {
      return el.scrollHeight - el.scrollTop - el.clientHeight
    }
    const lastChild = el.lastElementChild
    if (!lastChild) return 0
    const boundary = bottomBoundaryElRef.current
      ? bottomBoundaryElRef.current.getBoundingClientRect().top
      : el.getBoundingClientRect().bottom
    return lastChild.getBoundingClientRect().bottom - boundary
  }

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return

    // Some layouts (the create-project modals) render a header/footer bar
    // that can overlap the top/bottom of this wrapper by a variable amount —
    // fixed-position bars overlap by an amount that depends on other spacer
    // elements, and normal in-flow bars overlap by whatever their own
    // rendered height happens to be. Rather than guess a static offset that
    // only holds for one specific viewport/content combination, measure the
    // real overlap against the wrapper and clear it exactly. No-op (falls
    // back to the CSS default of 0) for every other consumer of this
    // component that doesn't have `.orderpreviewModal`-style bars nearby.
    const wrapperEl = el.parentElement
    const modalRoot = wrapperEl?.closest(".orderpreviewModal")
    bottomBoundaryElRef.current = null
    hasBoundaryRef.current = false

    // This component mounts once per modal and persists across steps that
    // share it (e.g. create_project_home_page_modal.jsx's multi-step flow),
    // so a step without its own .modalHeader/.modalFooterHomepageCreateProject
    // etc. must explicitly clear any offset a previous step's header/footer
    // left behind — otherwise the arrow keeps that stale inline position
    // instead of falling back to the CSS default of 0.
    let topOffset = ""
    let bottomOffset = ""

    if (modalRoot) {
      const wrapperRect = wrapperEl.getBoundingClientRect()

      const headerEl = modalRoot.querySelector(".modalHeader")
      if (headerEl) {
        const overlap = headerEl.getBoundingClientRect().bottom - wrapperRect.top
        if (overlap > 0) topOffset = `${Math.ceil(overlap)}px`
      }

      const footerEl = modalRoot.querySelector(".modalFooterHomepageCreateProject, .signupPaymentFooter, .checkoutModalFooter")
      if (footerEl) {
        bottomBoundaryElRef.current = footerEl
        hasBoundaryRef.current = true
        const overlap = wrapperRect.bottom - footerEl.getBoundingClientRect().top
        if (overlap > 0) bottomOffset = `${Math.ceil(overlap)}px`
      }
    }

    if (topZoneRef.current) topZoneRef.current.style.top = topOffset
    if (bottomZoneRef.current) bottomZoneRef.current.style.bottom = bottomOffset

    setCanScrollUp(el.scrollTop > 0)
    setCanScrollDown(remainingScrollablePx(el) > 1)
  }, [scrollRef])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    updateScrollState()

    el.addEventListener("scroll", updateScrollState)
    // The container's own box size never changes (it's fixed by the flex
    // layout), so ResizeObserver alone won't catch content — e.g. images —
    // growing the scrollable area as it loads in. Listening for "load" in
    // the capture phase catches each descendant <img> finishing, even
    // though "load" doesn't bubble.
    el.addEventListener("load", updateScrollState, true)
    const resizeObserver = new ResizeObserver(updateScrollState)
    resizeObserver.observe(el)

    // Covers content that swaps in without images or a container resize —
    // e.g. a "Loading…" placeholder getting replaced by a fetched list.
    const mutationObserver = new MutationObserver(updateScrollState)
    mutationObserver.observe(el, { childList: true, subtree: true })

    // Web font loading doesn't fire "load"/resize/mutation events on the
    // container, but can still shift its layout (and therefore the height
    // this component's own position depends on) after our first measurement.
    let cancelled = false
    document.fonts?.ready?.then(() => {
      if (!cancelled) updateScrollState()
    })

    // Mobile browsers resize the visual viewport (address bar show/hide)
    // without necessarily resizing `el` itself in a way ResizeObserver
    // catches, so listen directly for that too.
    window.addEventListener("resize", updateScrollState)
    window.visualViewport?.addEventListener("resize", updateScrollState)

    return () => {
      cancelled = true
      el.removeEventListener("scroll", updateScrollState)
      el.removeEventListener("load", updateScrollState, true)
      resizeObserver.disconnect()
      mutationObserver.disconnect()
      window.removeEventListener("resize", updateScrollState)
      window.visualViewport?.removeEventListener("resize", updateScrollState)
    }
  }, [scrollRef, updateScrollState])

  const stopScrolling = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
  }

  const startScrolling = (direction) => {
    stopScrolling()
    scrollIntervalRef.current = setInterval(() => {
      const el = scrollRef.current
      if (!el) return
      if (direction === "up") {
        el.scrollTop -= SCROLL_SPEED_PX
      } else {
        // Don't auto-scroll past the real content — see remainingScrollablePx.
        if (remainingScrollablePx(el) <= 0) {
          stopScrolling()
          return
        }
        el.scrollTop += SCROLL_SPEED_PX
      }
    }, SCROLL_INTERVAL_MS)
  }

  useEffect(() => stopScrolling, [])

  // Always mount both zones (rather than conditionally rendering them) so a
  // zone never gets inserted into the DOM directly under an already-resting
  // cursor — that would fire a spurious mouseenter and start auto-scrolling
  // the instant the arrow appears. Visibility/interactivity is toggled with
  // style instead, which only affects real pointer movement going forward.
  return (
    <>
      <div
        ref={topZoneRef}
        className="scrollEdgeZone scrollEdgeZoneTop"
        style={canScrollUp ? undefined : { opacity: 0, pointerEvents: "none" }}
        onMouseEnter={() => startScrolling("up")}
        onMouseLeave={stopScrolling}
        onTouchStart={(e) => { e.preventDefault(); startScrolling("up") }}
        onTouchEnd={stopScrolling}
        onTouchCancel={stopScrolling}
      >
        <i className="fas fa-chevron-up" />
      </div>
      <div
        ref={bottomZoneRef}
        className="scrollEdgeZone scrollEdgeZoneBottom"
        style={canScrollDown ? undefined : { opacity: 0, pointerEvents: "none" }}
        onMouseEnter={() => startScrolling("down")}
        onMouseLeave={stopScrolling}
        onTouchStart={(e) => { e.preventDefault(); startScrolling("down") }}
        onTouchEnd={stopScrolling}
        onTouchCancel={stopScrolling}
      >
        <i className="fas fa-chevron-down" />
      </div>
    </>
  )
}
